import { IonAlert, IonDatetime, IonDatetimeButton, IonModal } from '@ionic/react';
import { DateTime } from 'luxon';
import { FullPost, calcDateSpanDescription, getNow } from 'model';
import { FC } from 'react';
import { PostContentsToHTML } from './PostContentsToHTML.js';
import { ProfilePic } from './ProfilePic.js';
import { IconQuirktoken } from './QuirkIcons/index.js';
import { TruncatedSummary } from './TruncatedSummary.js';
// eslint-disable-next-line css-modules/no-unused-class
import sharedstyles from '../styles/Shared.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
import styles from './PromotionCreate.module.css';

export interface PromotionCreateProps {
	post: FullPost;
	promotionStartDate: string | null;
	promotionEndDate: string | null;
	setPromotionDates: React.Dispatch<
		React.SetStateAction<{
			promotionStartDate: string | null;
			promotionEndDate: string | null;
		}>
	>;
	promotionDates: {
		promotionStartDate: string | null;
		promotionEndDate: string | null;
	};
	totalTokenCost: number;
	balanceAfterPromotion: number;
	setShowAlert: (val: boolean) => void;
	showAlert: boolean;
}

export const PromotionCreate: FC<PromotionCreateProps> = ({
	post,
	promotionStartDate,
	promotionEndDate,
	setPromotionDates,
	promotionDates,
	totalTokenCost,
	balanceAfterPromotion,
	setShowAlert,
	showAlert
}) => {
	const isDateAfter = (date1: string | undefined | null, date2: string | undefined | null) => {
		if (!date1 || !date2) return false;
		const parsedDate1 = DateTime.fromISO(date1);
		const parsedDate2 = DateTime.fromISO(date2);
		return parsedDate1 > parsedDate2;
	};

	return (
		<>
			<div className={sharedstyles.userContainer}>
				<ProfilePic user={post.fullAuthor} />
				<div className="fullwidth">
					<div className={sharedstyles.name}>{post.fullAuthor.username}</div>
					<div className={sharedstyles.time}>{calcDateSpanDescription(post?.fullAuthor.updated)}</div>
				</div>
			</div>
			<h4>Promote this post</h4>
			<div className="space10" />
			<TruncatedSummary>
				<PostContentsToHTML>{post.contents}</PostContentsToHTML>
			</TruncatedSummary>
			<div className={styles.promotionContainer}>
				<div className={styles.promotedFieldContainer}>
					<div className={styles.dualDateContainer}>
						{promotionStartDate && (
							<div className={formStyles.fieldContainer} key={'startDate' + promotionStartDate}>
								<div className={formStyles.label}>Promotion Start Date</div>
								<IonDatetimeButton
									placeholder="Select a start date"
									datetime="promotion-start-date"
									style={{ background: `var(--ion-color-primary-contrast)` }}
									className={formStyles.dateTimeButton}
								/>

								<IonModal keepContentsMounted>
									<IonDatetime
										presentation="date"
										min={getNow()}
										id="promotion-start-date"
										value={promotionStartDate}
										onIonChange={e => {
											setPromotionDates({
												...promotionDates,
												promotionStartDate: (e.target.value as string).substring(0, 10)
											});

											setPromotionDates(value => {
												if (isDateAfter(value.promotionStartDate, value.promotionEndDate)) {
													return {
														...value,
														promotionEndDate: DateTime.fromISO(
															value.promotionStartDate || DateTime.now().toISO()
														)
															.plus({ days: 1 })
															.toISO()
															.substring(0, 10)
													};
												}
												return value;
											});
										}}
										minuteValues="0,15,30,45"
										disabled={false}
									></IonDatetime>
								</IonModal>
							</div>
						)}
						{promotionEndDate && (
							<div className={formStyles.fieldContainer} key={'endDate' + promotionEndDate}>
								<div className={formStyles.label}>Promotion End Date</div>
								<div className={styles.endDateRemoveField}>
									<IonDatetimeButton
										placeholder="Select an end date"
										datetime="promotion-end-date"
										style={{ background: `var(--ion-color-primary-contrast)` }}
										className={formStyles.dateTimeButton}
									/>

									<IonModal keepContentsMounted>
										<IonDatetime
											min={promotionStartDate as string}
											presentation="date"
											max={DateTime.fromISO(promotionStartDate as string)
												.toUTC()
												.plus({ weeks: 2 })
												.toISO()}
											id="promotion-end-date"
											value={promotionEndDate}
											onIonChange={e => {
												setPromotionDates({
													...promotionDates,
													promotionEndDate: (e.target.value as string).substring(0, 10)
												});
											}}
											minuteValues="0,15,30,45"
											disabled={false}
										/>
									</IonModal>
								</div>
							</div>
						)}
					</div>
					<div className={styles.priceContainer}>
						<div className={styles.priceTitle}>Total tokens:</div>
						<div className={styles.price}>
							<div className="quirkToken">
								<IconQuirktoken />
							</div>
							{totalTokenCost}
						</div>
					</div>
					<hr />
					<div className="space10" />
					{balanceAfterPromotion >= 0 ? (
						<div className={styles.priceContainer}>
							<div>Account Balance:</div>
							<div className={styles.balance}>
								<div className="quirkToken">
									<IconQuirktoken />
								</div>
								{balanceAfterPromotion.toLocaleString()}
							</div>
						</div>
					) : (
						<>
							<div className={styles.insuffientFunds}>
								<div className={styles.priceContainer}>
									<div>Not enough tokens:</div>
									<div className={styles.balance}>
										<div className="quirkToken">
											<IconQuirktoken />
										</div>
										{balanceAfterPromotion}
									</div>
								</div>
							</div>
						</>
					)}
					<div className="space20" />
				</div>
			</div>
			<IonAlert
				header="Insufficient funds"
				message={'You need more tokens to promote this post. Reduce the number of days and try again.'}
				buttons={[
					{
						text: 'OK',
						handler: () => {
							setShowAlert(false);
						}
					}
				]}
				isOpen={showAlert}
			></IonAlert>
		</>
	);
};
